import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { parseISO } from 'date-fns';
import { originalFilenameFromUrl, ensurePdfExtension } from '../../utils/documentName.js';
import { stableDocumentKeyFromUrl } from '../../utils/documentIdentity.js';
import { usePdfDocumentSource, getPdfBytes } from '../../utils/pdfDocumentSource.js';
import {
    Box,
    Paper,
    Toolbar,
    IconButton,
    Button,
    TextField,
    Typography,
    Divider,
    InputAdornment,
    Tooltip,
    CircularProgress,
} from '@mui/material';
import {
    ChevronLeft,
    ChevronRight,
    ZoomIn,
    ZoomOut,
    Clear,
    Download,
    Search,
    KeyboardArrowUp,
    KeyboardArrowDown,
    FitScreen,
    RestorePage,
    WidthFull,
} from '@mui/icons-material';

// Configure PDF.js worker.
// The frontend package.json postinstall copies pdfjs-dist's worker file
// (pdf.worker.min.mjs in v5+) into public/pdf.worker.min.js — note the
// rename. PDF.js's worker loader handles the file content correctly via
// the .js extension; do NOT change this to .mjs without verifying the
// CRA dev server / production CloudFront serves .mjs with the right
// content-type for module workers. Keep in sync with DocumentSplitEditor.js.
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';
// PDF document options - constant never changes
const PDF_DOCUMENT_OPTIONS = {
    cMapUrl: 'cmaps/',
    cMapPacked: true,
};

// helper function
const BBOX_IDX_LEFT = 0
const BBOX_IDX_TOP = 1
const BBOX_IDX_RIGHT = 2
const BBOX_IDX_BOTTOM = 3

const mapOcrolusBboxToViewerBbox = (bbox) => {
    if (!bbox) {
        return null;
    }
    return {
        x: bbox[BBOX_IDX_LEFT],
        y: bbox[BBOX_IDX_TOP],
        width: bbox[BBOX_IDX_RIGHT] - bbox[BBOX_IDX_LEFT],
        height: bbox[BBOX_IDX_BOTTOM] - bbox[BBOX_IDX_TOP],
    };
}

/**
 * Sort search results by position: page number, then y position (top to bottom), then x position (left to right)
 * @param {Array} results - Array of search results with page, boundingBox.x, boundingBox.y
 * @returns {Array} Sorted results
 */
const sortResultsByPosition = (results) => {
    return results.sort((a, b) => {
        // First sort by page number
        if (a.page !== b.page) {
            return a.page - b.page;
        }

        // Then sort by Y position (top to bottom)
        // Note: In PDF coordinates, y increases from bottom to top, but boundingBox.y is already converted
        // to screen coordinates where y increases from top to bottom
        const yDiff = a.boundingBox.y - b.boundingBox.y;

        // If Y positions are similar (within 5 pixels), consider them on the same line
        // and sort by X position (left to right)
        if (Math.abs(yDiff) < 5) {
            return a.boundingBox.x - b.boundingBox.x;
        }

        return yDiff;
    });
};

/**
 * PdfViewer Component with Material UI
 *
 * @param {string} url - URL to the PDF file
 * @param {number} displayPage - Page number to display (default: 1)
 * @param {object} boundingBox - Optional bounding box to highlight on current page { x, y, width, height } or [left, top, right, bottom]
 * @param {function} onPageChange - Callback when page changes
 * @param {string} className - Additional CSS classes
 * @param {object} transactionSearch - Optional transaction search { descr: string, amount: number, posting_date: string|Date }
 * @param {string} documentKey - Optional stable key to identify the document. When omitted,
 *   the key is derived from the URL path (query string dropped), so a re-signed S3 URL for
 *   the same object does NOT count as a different document and does not trigger a reload.
 * @param {string} documentName - Optional document name for display and download filename
 */
const PdfViewer = ({
    url,
    displayPage = 1,
    boundingBox = null,
    onPageChange,
    className = '',
    transactionSearch = null,
    documentKey = null,
    documentName = null,
    navigationKey = null,
    resetKey = null,
    // Opt-in: after the highlighted line renders, scroll the container so it's
    // visible without manual scrolling (used by the side-by-side transfer review).
    scrollHighlightIntoView = false,
    // Bump this to re-run the auto-scroll on demand (e.g. user re-clicks the row
    // to recenter after scrolling away). Changing it re-fires even on the same doc/page.
    scrollHighlightNonce = null
}) => {
    const [pageLoading, setPageLoading] = useState(true);
    const [downloadError, setDownloadError] = useState(null);
    const [numPages, setNumPages] = useState(null);
    const [pageNumber, setPageNumber] = useState(displayPage);
    const [scale, setScale] = useState(1.0);
    const [fitMode, setFitMode] = useState('custom'); // 'custom', 'width', 'page'
    // const [pageWidth, setPageWidth] = useState(null);
    // const [pageHeight, setPageHeight] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [canSearch, setCanSearch] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const [searchResults, setSearchResults] = useState([]);
    const [currentSearchIndex, setCurrentSearchIndex] = useState(0);
    const [searchHighlight, setSearchHighlight] = useState(null);
    const [currentViewport, setCurrentViewport] = useState(null);
    const pdfDocumentRef = useRef(null);
    // const pageRefs = useRef({});
    const containerRef = useRef(null);
    const pageContainerRef = useRef(null);
    const prevDisplayPage = useRef(displayPage);
    const searchRunningRef = useRef(false);
    const lastTransactionSearchRef = useRef(null);
    const prevDocumentKeyRef = useRef(documentKey);
    const lastScrollPaginationRef = useRef(0); // Track last scroll pagination time
    const boundingBoxPageRef = useRef(displayPage); // Track which page the boundingBox is for
    const prevResetKeyRef = useRef(resetKey);

    // Use documentKey (if provided), else derive the identity from the URL path. A
    // pre-signed S3 URL carries a fresh signature + expiry in its query string every
    // time it is minted for the SAME object, so the raw URL string is not a document
    // identity — dropping the query yields one (Trello IYmLT1Pl).
    const stableDocumentKey = documentKey || stableDocumentKeyFromUrl(url) || url;

    if (boundingBox && Array.isArray(boundingBox) && boundingBox.length === 4) {
        // Convert [left, top, right, bottom] to { x, y, width, height }
        boundingBox = mapOcrolusBboxToViewerBbox(boundingBox);
    }

    // An authoritative coordinate bbox (the transaction's stored highlight) supersedes
    // the fuzzy description+amount fallback search below. Track its presence so the
    // fallback is skipped when we already have exact coordinates. Boolean so it stays
    // referentially stable across renders (boundingBox is a fresh object each render).
    const hasBoundingBox = !!boundingBox;

    // The `file` prop handed to react-pdf: a stable `{ data: ArrayBuffer }` per
    // document identity, sourced through the shared byte cache (see
    // utils/pdfDocumentSource.js). We fetch ourselves instead of letting pdf.js
    // fetch the URL, because a component remount mid-load destroyed pdf.js's
    // loading task and its silently-dropped promise — the "Loading PDF..." that
    // never resolved with zero network traffic (bytes served from the browser's
    // HTTP cache). With a cached byte hand-off there is no in-flight fetch to
    // lose, and a real failure lands in `pdfError` below instead of nowhere.
    const { file: documentFile, isDocumentLoading: documentLoading, error: pdfError, retry: retryPdfLoad } =
        usePdfDocumentSource(stableDocumentKey, url);

    useEffect(() => {
        if (resetKey !== prevResetKeyRef.current) {
            prevResetKeyRef.current = resetKey;
            // Clear highlight state but don't reload PDF
            setSearchHighlight(null);
            setSearchResults([]);
            setCurrentSearchIndex(0);
            lastTransactionSearchRef.current = null;
        }
    }, [resetKey]);

    useEffect(() => {
        if (prevDocumentKeyRef.current !== stableDocumentKey) {
            prevDocumentKeyRef.current = stableDocumentKey;
            setPageLoading(true);
            setDownloadError(null); // stale message must not follow a new document
        }
    }, [stableDocumentKey]);

    useEffect(() => {
        const captureViewport = async () => {
            if (!pdfDocumentRef.current || !numPages || !pageNumber) {
                //console.log('Cannot capture viewport - PDF not ready:', {
                //     hasDoc: !!pdfDocumentRef.current,
                //     numPages,
                //     pageNumber
                // });
                return;
            }

            try {
                //console.log('Attempting to capture viewport for page', pageNumber);
                const page = await pdfDocumentRef.current.getPage(pageNumber);
                const viewport = page.getViewport({ scale: 1 });

                const viewportInfo = {
                    width: viewport.width,
                    height: viewport.height,
                    scale: viewport.scale
                };

                //console.log('Captured viewport:', viewportInfo);
                setCurrentViewport(viewportInfo);
            } catch (error) {
                console.error('Error capturing viewport:', error);
                setCurrentViewport(null);
            }
        };

        captureViewport();
    }, [pageNumber, numPages]);

    // Update page number when displayPage prop changes (not when internal pageNumber changes)

    const prevNavigationKeyRef = useRef(navigationKey);

    useEffect(() => {
        const displayPageChanged = displayPage !== prevDisplayPage.current;
        const navigationKeyChanged = navigationKey !== prevNavigationKeyRef.current;

        if (displayPageChanged || navigationKeyChanged) {
            prevDisplayPage.current = displayPage;
            prevNavigationKeyRef.current = navigationKey;
            boundingBoxPageRef.current = displayPage;

            if (numPages && displayPage >= 1 && displayPage <= numPages) {
                setPageNumber(displayPage);
            }
        }
    }, [displayPage, numPages, navigationKey]);

    // Separate effect: Apply pending displayPage when PDF finishes loading
    useEffect(() => {
        // When numPages first becomes available, sync to the displayPage prop
        // This handles the case where displayPage was set before PDF loaded
        if (numPages && prevDisplayPage.current >= 1 && prevDisplayPage.current <= numPages) {
            if (pageNumber !== prevDisplayPage.current) {
                setPageNumber(prevDisplayPage.current);
            }
        }
    }, [numPages]); // Only run when numPages changes (i.e., PDF loads)

    // Reset page loading state when page changes
    useEffect(() => {
        setPageLoading(true);
    }, [pageNumber]);

    const pageContainsAnyText = useCallback(async (pageNum) => {
        try {
            if (!pdfDocumentRef.current) return false;

            const page = await pdfDocumentRef.current.getPage(pageNum);
            const textContent = await page.getTextContent();
            // //console.log(`Page ${pageNum} text items count: ${textContent?.items?.length}`);
            if (textContent?.items?.length > 0) {
                // //console.log(`Sample text items on page ${pageNum}: ${JSON.stringify(textContent.items.slice(0, 5).map(item => item.str))}`);
                return true;
            }
            return false;
        } catch (error) {
            console.error(`Error checking text on page ${pageNum}:`, error);
            return false;
        }
    }, []);

    // Handle PDF document load success
    const onDocumentLoadSuccess = useCallback(async (pdf) => {
        setNumPages(pdf.numPages);
        // Store in ref to avoid React state issues with PDF.js
        pdfDocumentRef.current = pdf;
        try {
            // Check pages sequentially and stop at first page with text
            let canSearch = false;
            for (let i = 1; i <= pdf.numPages; i++) {
                const hasText = await pageContainsAnyText(i);
                // //console.log(`Checked page ${i}, hasText=${hasText}`);
                if (hasText) {
                    // //console.log(`PDF is searchable, text found on page ${i}`);
                    canSearch = true;
                    break; // Stop checking once we find text
                }
            }

            // Update state with the result
            //console.log('PDF searchability check complete:', canSearch);
            setCanSearch(canSearch);
        } catch (error) {
            console.error('Error checking PDF searchability:', error);
            setCanSearch(false);
        }

    }, [pageContainsAnyText]);

    // Navigation handlers
    const goToPreviousPage = useCallback(() => {
        setPageNumber(prev => Math.max(1, prev - 1));
    }, []);

    const goToNextPage = useCallback(() => {
        setPageNumber(prev => {
            if (!numPages) return prev;
            return Math.min(numPages, prev + 1);
        });
    }, [numPages]);

    const goToPage = useCallback((page) => {
        const pageNum = parseInt(page, 10);
        if (pageNum >= 1 && numPages && pageNum <= numPages) {
            setPageNumber(pageNum);
        }
    }, [numPages]);

    // Notify parent component of page changes
    useEffect(() => {
        if (onPageChange && numPages) {
            onPageChange(pageNumber);
        }
    }, [pageNumber, onPageChange, numPages]);

    // Zoom handlers
    const zoomIn = useCallback(() => {
        setFitMode('custom');
        setScale(prev => Math.min(prev + 0.2, 3.0));
    }, []);

    const zoomOut = useCallback(() => {
        setFitMode('custom');
        setScale(prev => Math.max(prev - 0.2, 0.5));
    }, []);

    const resetZoom = useCallback(() => {
        setFitMode('custom');
        setScale(1.0);
    }, []);

    // Fit mode handlers
    const fitToWidth = useCallback(() => {
        if (containerRef.current && currentViewport) {
            const containerWidth = containerRef.current.clientWidth - 48; // Account for padding
            const newScale = containerWidth / currentViewport.width;
            // Only update if scale actually changes significantly (prevents floating point loops)
            if (Math.abs(newScale - scale) > 0.001) {
                setScale(newScale);
            }
            setFitMode('width');
        }
    }, [currentViewport, scale]);

    const fitToPage = useCallback(() => {
        if (containerRef.current && currentViewport) {
            const containerWidth = containerRef.current.clientWidth - 48;
            const containerHeight = containerRef.current.clientHeight - 48;
            const scaleWidth = containerWidth / currentViewport.width;
            const scaleHeight = containerHeight / currentViewport.height;
            const newScale = Math.min(scaleWidth, scaleHeight);
            // Only update if scale actually changes significantly
            if (Math.abs(newScale - scale) > 0.001) {
                setScale(newScale);
            }
            setFitMode('page');
        }
    }, [currentViewport, scale]);

    // Recalculate fit when container resizes
    useEffect(() => {
        if (!containerRef.current) return;

        let resizeTimeout = null;

        const resizeObserver = new ResizeObserver(() => {
            // Debounce resize handling to prevent loops
            if (resizeTimeout) {
                clearTimeout(resizeTimeout);
            }
            resizeTimeout = setTimeout(() => {
                if (fitMode === 'width') {
                    fitToWidth();
                } else if (fitMode === 'page') {
                    fitToPage();
                }
            }, 150); // Wait for layout to stabilize
        });

        resizeObserver.observe(containerRef.current);

        return () => {
            if (resizeTimeout) {
                clearTimeout(resizeTimeout);
            }
            resizeObserver.disconnect();
        };
    }, [fitMode, fitToWidth, fitToPage]);

    // Download handler
    const downloadPdf = useCallback(async () => {
        setDownloadError(null);
        try {
            // Save the SAME bytes the viewer is displaying, via the shared cache: a
            // cache hit needs no network at all, so a pre-signed URL that expired
            // while the reviewer worked cannot turn the download into an S3 error
            // document. Previously this did a bare `fetch(url)` and handed
            // `response.blob()` straight to a `.pdf` download — and because fetch()
            // does not reject on 4xx, an expired URL saved a 353-byte
            // <Error>Request has expired</Error> XML file named like the statement.
            // On a cache miss getPdfBytes validates both the status and the %PDF
            // header, and throws rather than returning an error body.
            const buffer = await getPdfBytes(stableDocumentKey, url);
            // Blob() copies the bytes, so the cached master is never handed out or
            // detached (see the copy note in usePdfDocumentSource).
            const blob = new Blob([buffer], { type: 'application/pdf' });
            const blobUrl = window.URL.createObjectURL(blob);

            // Prefer an explicit documentName; otherwise recover the original
            // upload filename from the signed URL path (NOT `url.split('/').pop()`,
            // which yields the S3 key + query string — garbage in the save prompt).
            // Always land on a `.pdf` extension so the browser reports the type.
            const downloadName = ensurePdfExtension(
                documentName || originalFilenameFromUrl(url) || 'document.pdf'
            );

            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = downloadName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Clean up blob URL
            window.URL.revokeObjectURL(blobUrl);
        } catch (error) {
            // Deliberately NO fallback to window.open(url): when the cause is an
            // expired signed URL, opening it just shows the same S3 error XML in a
            // tab. Surface the failure instead of producing a bad artifact.
            console.error('Could not download PDF:', error);
            setDownloadError(
                'Download failed — the document link may have expired. Reload the document, then try again.'
            );
        }
    }, [stableDocumentKey, url, documentName]);

    // Normalize text for better matching
    const normalizeText = (text) => {
        return text
            .toLowerCase()
            .replace(/[$,.\-+]/g, '') // Remove $, commas, periods, hyphens
            .replace(/\s+/g, ' ') // Normalize whitespace
            .trim();
    };

    // Search functionality
    const performSearch = useCallback(async () => {
        if (!searchText || !pdfDocumentRef.current) {
            return;
        }

        setIsSearching(true);
        const results = [];

        try {
            // Check if this is a proximity search (multiple terms separated by spaces)
            const searchTerms = searchText.toLowerCase().trim().split(/\s+/);
            // const isProximitySearch = searchTerms.length > 1;

            for (let i = 1; i <= numPages; i++) {
                const page = await pdfDocumentRef.current.getPage(i);
                const textContent = await page.getTextContent();
                const viewport = page.getViewport({ scale: 1 });

                performSimpleSearch(textContent, viewport, searchText.toLowerCase(), i, results);
                // if (isProximitySearch) {
                //     // Proximity search: find items where all terms appear nearby
                //     performProximitySearch(textContent, viewport, searchTerms, i, results);
                // } else {
                //     // Simple single-term search
                //     performSimpleSearch(textContent, viewport, searchText.toLowerCase(), i, results);
                // }
            }

            // MODIFICATION: Sort results by position before setting them
            const sortedResults = sortResultsByPosition(results);
            setSearchResults(sortedResults);
            setCurrentSearchIndex(0);

            if (sortedResults.length > 0) {
                setPageNumber(sortedResults[0].page);
                setSearchHighlight(sortedResults[0].boundingBox);
            } else {
                setSearchHighlight(null);
            }
        } catch (error) {
            console.error('Search error:', error);
        } finally {
            setIsSearching(false);
        }
    }, [searchText, numPages]);

    // Simple search for a single term
    const performSimpleSearch = (textContent, viewport, searchLower, pageNum, results) => {
        for (let itemIdx = 0; itemIdx < textContent.items.length; itemIdx++) {
            const item = textContent.items[itemIdx];
            const itemText = item.str;
            const itemLower = itemText.toLowerCase();

            if (itemLower.includes(searchLower)) {
                const transform = item.transform;
                const x = transform[4];
                const y = viewport.height - transform[5];
                const width = item.width;
                const height = item.height;

                results.push({
                    page: pageNum,
                    textIndex: itemIdx,
                    boundingBox: {
                        x: x,
                        y: y - height,
                        width: width,
                        height: height
                    }
                });
            }
        }
    };

    // FIXED: Proximity search - find groups of items where all terms appear nearby
    const performProximitySearch = (textContent, viewport, searchTerms, pageNum, results) => {
        console.log(`Performing proximity search on page ${pageNum} for terms:`, searchTerms);
        const maxDistance = 20; // Distance threshold for proximity

        // Build array of items with positions and matching terms
        const itemsWithMatches = textContent.items.map((item, idx) => {
            const itemText = item.str.toLowerCase();
            const itemNormalized = normalizeText(item.str);

            // IMPORTANT FIX: Skip empty/whitespace-only items
            if (!itemNormalized || itemNormalized.trim() === '') {
                return null;
            }

            // Check which search terms this item matches
            const matchedTerms = searchTerms.filter(term => {
                const normalizedTerm = normalizeText(term);

                // IMPORTANT FIX: Don't use includes with empty strings
                if (!normalizedTerm || normalizedTerm.trim() === '') {
                    return false;
                }

                return itemNormalized.includes(normalizedTerm) ||
                    itemText.includes(term);
            });

            return {
                index: idx,
                item: item,
                text: item.str,
                x: item.transform[4],
                y: item.transform[5],
                matchedTerms: matchedTerms
            };
        }).filter(item => item !== null && item.matchedTerms.length > 0);

        //console.log(`Page ${pageNum}: Found ${itemsWithMatches.length} items with matches`);

        // FIXED: Track which items have been used in a result to avoid duplicates
        const usedItems = new Set();

        // For each item that matches at least one search term
        for (const matchItem of itemsWithMatches) {
            // Skip if this item was already used in a result
            if (usedItems.has(matchItem.index)) {
                continue;
            }

            // Find all items within maxDistance of this item
            const nearbyItems = itemsWithMatches.filter(other => {
                if (other.index === matchItem.index) return true;
                const distance = Math.sqrt(
                    Math.pow(other.x - matchItem.x, 2) +
                    Math.pow(other.y - matchItem.y, 2)
                );
                return distance <= maxDistance;
            });

            // Count how many unique search terms are covered by nearby items
            const foundTerms = new Set();
            nearbyItems.forEach(item => {
                item.matchedTerms.forEach(term => foundTerms.add(term));
            });

            // If all search terms are found nearby, add as a result
            if (foundTerms.size === searchTerms.length) {
                // Mark all these items as used
                nearbyItems.forEach(item => usedItems.add(item.index));

                // Calculate bounding box that encompasses all matched items
                const allX = nearbyItems.map(item => item.x);
                const allY = nearbyItems.map(item => item.y);
                const allWidths = nearbyItems.map(item => item.item.width);
                const allHeights = nearbyItems.map(item => item.item.height);

                const minX = Math.min(...allX);
                const maxX = Math.max(...allX.map((x, idx) => x + allWidths[idx]));
                const minY = Math.min(...allY);
                const maxY = Math.max(...allY.map((y, idx) => y + allHeights[idx]));

                const x = minX;
                const y = viewport.height - maxY;
                const width = maxX - minX;
                const height = maxY - minY;

                const result = {
                    page: pageNum,
                    textIndex: matchItem.index,
                    boundingBox: {
                        x: x,
                        y: y,
                        width: width,
                        height: height
                    },
                    matchedTerms: Array.from(foundTerms),
                    preview: nearbyItems.map(item => item.text).join(' ')
                };

                results.push(result);
                console.log('Added result:', result);
            }
        }
    };

    // Transaction search: find description + amount matches
    const performTransactionSearch = useCallback(async () => {
        if (!transactionSearch || !pdfDocumentRef.current || searchRunningRef.current) {
            return;
        }

        const { descr, amount, posting_date } = transactionSearch;
        if (!descr || amount === undefined || amount === null) {
            return;
        }

        searchRunningRef.current = true;
        setIsSearching(true);
        let results = [];

        try {
            // Normalize amount: convert to cents and remove leading zeros
            const normalizedAmount = Math.abs(Math.round(amount * 100)).toString();

            // Parse date if provided
            let dateVariants = null;
            if (posting_date) {
                const date = typeof posting_date === 'string' ? parseISO(posting_date) : posting_date;
                if (!isNaN(date.getTime())) {
                    const month = date.getMonth() + 1; // 1-12
                    const monthLeadingzero = month < 10 ? `0${month}` : month.toString();
                    const day = date.getDate();
                    const dayLeadingzero = day < 10 ? `0${day}` : day.toString();
                    const year = date.getFullYear();
                    const year2Digit = year.toString().slice(-2);

                    const monthNames = ['', 'January', 'February', 'March', 'April', 'May', 'June',
                        'July', 'August', 'September', 'October', 'November', 'December'];
                    const monthAbbrevs = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

                    dateVariants = {
                        month: [month.toString(), monthLeadingzero, monthNames[month].toLowerCase(), monthAbbrevs[month].toLowerCase()],
                        day: [day.toString(), dayLeadingzero],
                        year: [year.toString(), year2Digit]
                    };

                    //console.log('Date variants:', dateVariants);
                }
            }

            // Helper function to search with given vertical distance
            const searchWithVerticalDistance = async (maxVerticalDist, attemptLabel) => {
                //console.log(`${attemptLabel}: searching with maxVerticalDistance=${maxVerticalDist}px`);
                const pageResults = [];

                for (let i = 1; i <= numPages; i++) {
                    try {
                        const page = await pdfDocumentRef.current.getPage(i);
                        const textContent = await page.getTextContent();
                        const viewport = page.getViewport({ scale: 1 });

                        //console.log(`textContent.items.length on page ${i}: ${textContent.items.length}`);

                        // Find all items with their positions
                        const allItems = textContent.items.map((item, idx) => {
                            const normalized = normalizeText(item.str);
                            // For amounts, also create a version with leading zeros removed for comparison
                            const normalizedAsNumber = parseInt(normalized, 10);
                            const normalizedAmount = isNaN(normalizedAsNumber) ? null : normalizedAsNumber.toString();

                            return {
                                index: idx,
                                item: item,
                                text: item.str,
                                normalized: normalized,
                                normalizedAmount: normalizedAmount,
                                lower: item.str.toLowerCase(),
                                x: item.transform[4],
                                y: item.transform[5],
                                width: item.width,
                                height: item.height
                            };
                        });
                        //console.log(`Page ${i}: extracted ${allItems.length} text items`);

                        // Find items that match the amount (compare as numbers to ignore leading zeros)
                        const amountMatches = allItems.filter(item =>
                            item.normalizedAmount === normalizedAmount
                        );

                        if (true || i === 1) {
                            //console.log(`Page ${i}: Searching for amount "${amount}" (normalized: "${normalizedAmount}")`);
                            //console.log(allItems.slice(0, 100).map(item => ({ text: item.text, normalized: item.normalized, normalizedAmount: item.normalizedAmount })));
                        }

                        //console.log(`Page ${i}: Found ${amountMatches.length} amount matches for ${amount} (normalized: ${normalizedAmount})`);
                        if (true || (amountMatches.length > 0 && i === 1)) {
                            //console.log('Sample amount matches:', amountMatches.slice(0, 3).map(m => ({ text: m.text, normalized: m.normalized, normalizedAmount: m.normalizedAmount })));
                        }

                        // For each amount match, look for description terms near the amount
                        for (const amountItem of amountMatches) {
                            const maxHorizontalDistance = 1200; // Full width of statement

                            // Find all items near this amount using the current vertical distance
                            const sameLineItems = allItems.filter(item => {
                                if (item.index === amountItem.index) return false;
                                const verticalDistance = Math.abs(item.y - amountItem.y);
                                const horizontalDistance = Math.abs(item.x - amountItem.x);
                                return verticalDistance <= maxVerticalDist &&
                                    horizontalDistance <= maxHorizontalDistance;
                            });

                            // Concatenate all text near the amount
                            const lineText = sameLineItems.map(item => item.text).join(' ').toLowerCase();
                            const lineNormalized = normalizeText(lineText);

                            //console.log(`Amount ${amountItem.text} at y=${amountItem.y}: Nearby text (${sameLineItems.length} items, within ${maxVerticalDist}px) = "${lineText}"`);

                            // Count how many description terms match in nearby text
                            let matchedTermsCount = 0;
                            const matchedItems = [];

                            for (const term of descrTerms) {
                                const normalizedTerm = normalizeText(term);
                                if (lineNormalized.includes(normalizedTerm) || lineText.includes(term)) {
                                    matchedTermsCount++;

                                    // Find which items contain this term
                                    for (const item of sameLineItems) {
                                        if ((item.normalized.includes(normalizedTerm) || item.lower.includes(term)) &&
                                            !matchedItems.some(mi => mi.index === item.index)) {
                                            matchedItems.push(item);
                                        }
                                    }
                                }
                            }

                            // Check date match if date is provided
                            let dateMatches = false;
                            const dateMatchedItems = [];

                            if (dateVariants) {
                                let monthMatch = false;
                                let dayMatch = false;
                                let yearMatch = false;

                                // Create a space-separated and slash-separated version for better matching
                                const lineTextSpaced = lineText.replace(/\//g, ' / ').replace(/\s+/g, ' ');

                                // Check if month is on this line
                                for (const monthVariant of dateVariants.month) {
                                    // For numeric months (1-2 digits), extract numbers; for text months, use includes
                                    if (/^\d{1,2}$/.test(monthVariant)) {
                                        const numbers = lineText.match(/\d+/g) || [];
                                        if (numbers.includes(monthVariant)) {
                                            monthMatch = true;
                                            // Find items containing month
                                            for (const item of sameLineItems) {
                                                const itemNumbers = item.text.match(/\d+/g) || [];
                                                if (itemNumbers.includes(monthVariant) &&
                                                    !dateMatchedItems.some(mi => mi.index === item.index) &&
                                                    !matchedItems.some(mi => mi.index === item.index)) {
                                                    dateMatchedItems.push(item);
                                                }
                                            }
                                            break;
                                        }
                                    } else {
                                        // Text month name (January, Feb, etc)
                                        if (lineText.includes(monthVariant) || lineNormalized.includes(normalizeText(monthVariant))) {
                                            monthMatch = true;
                                            // Find items containing month
                                            for (const item of sameLineItems) {
                                                if ((item.lower.includes(monthVariant) || item.normalized.includes(normalizeText(monthVariant))) &&
                                                    !dateMatchedItems.some(mi => mi.index === item.index) &&
                                                    !matchedItems.some(mi => mi.index === item.index)) {
                                                    dateMatchedItems.push(item);
                                                }
                                            }
                                            break;
                                        }
                                    }
                                }

                                // Check if day is on this line - use word boundaries
                                for (const dayVariant of dateVariants.day) {
                                    // Split by non-digits to find day as separate number
                                    const numbers = lineText.match(/\d+/g) || [];
                                    if (numbers.includes(dayVariant)) {
                                        dayMatch = true;
                                        // Find items containing day
                                        for (const item of sameLineItems) {
                                            const itemNumbers = item.text.match(/\d+/g) || [];
                                            if (itemNumbers.includes(dayVariant) &&
                                                !dateMatchedItems.some(mi => mi.index === item.index) &&
                                                !matchedItems.some(mi => mi.index === item.index)) {
                                                dateMatchedItems.push(item);
                                            }
                                        }
                                        break;
                                    }
                                }

                                // Check if year is on this line - use word boundaries
                                for (const yearVariant of dateVariants.year) {
                                    // Split by non-digits to find year as separate number
                                    const numbers = lineText.match(/\d+/g) || [];
                                    if (numbers.includes(yearVariant)) {
                                        yearMatch = true;
                                        // Find items containing year
                                        for (const item of sameLineItems) {
                                            const itemNumbers = item.text.match(/\d+/g) || [];
                                            if (itemNumbers.includes(yearVariant) &&
                                                !dateMatchedItems.some(mi => mi.index === item.index) &&
                                                !matchedItems.some(mi => mi.index === item.index)) {
                                                dateMatchedItems.push(item);
                                            }
                                        }
                                        break;
                                    }
                                }

                                // Match if all 3 components found, OR if month+day found and year is missing from line
                                // (Some bank statements don't include year on each transaction line)
                                dateMatches = (monthMatch && dayMatch && yearMatch) || (monthMatch && dayMatch && !yearMatch);
                                //console.log(`Date match: month=${monthMatch}, day=${dayMatch}, year=${yearMatch} (${yearMatch ? 'full match' : 'month+day only'}), looking for ${JSON.stringify(dateVariants)}`);
                            } else {
                                // If no date provided, consider it a match
                                dateMatches = true;
                            }

                            // Calculate match percentage - require at least 60% of terms to match
                            const matchPercentage = matchedTermsCount / descrTerms.length;
                            const minMatchThreshold = 0.6;

                            //console.log(`Matched ${matchedTermsCount}/${descrTerms.length} terms (${(matchPercentage * 100).toFixed(1)}%)`);

                            if (matchPercentage >= minMatchThreshold && matchedItems.length > 0 && dateMatches) {
                                // Include the amount item and date items in the bounding box
                                const allMatchItems = [...matchedItems, ...dateMatchedItems, amountItem];

                                const allX = allMatchItems.map(item => item.x);
                                const allY = allMatchItems.map(item => item.y);
                                const allWidths = allMatchItems.map(item => item.width);
                                const allHeights = allMatchItems.map(item => item.height);

                                const minX = Math.min(...allX);
                                const maxX = Math.max(...allX.map((x, idx) => x + allWidths[idx]));
                                const minY = Math.min(...allY);
                                const maxY = Math.max(...allY.map((y, idx) => y + allHeights[idx]));

                                const x = minX;
                                const y = viewport.height - maxY;
                                const width = maxX - minX;
                                const height = maxY - minY;

                                const result = {
                                    page: i,
                                    textIndex: amountItem.index,
                                    boundingBox: {
                                        x: x,
                                        y: y,
                                        width: width,
                                        height: height
                                    },
                                    matchedAmount: amountItem.text,
                                    matchedDescription: matchedItems.map(item => item.text).join(' '),
                                    matchedDate: dateMatchedItems.map(item => item.text).join(' '),
                                    matchScore: matchPercentage,
                                    preview: allMatchItems.map(item => item.text).join(' ')
                                };

                                pageResults.push(result);
                                //console.log('Added transaction result:', result);
                            }
                        }
                    } catch (pageError) {
                        console.error(`Error processing page ${i}:`, pageError);
                        // Continue with next page
                    }
                }

                return pageResults;
            };

            // Iteratively try with increasing vertical distances: 5px, 10px, 15px
            const descrTerms = descr.toLowerCase().trim().split(/\s+/);
            //console.log('Transaction search:', { descr, amount, normalizedAmount, posting_date });

            const verticalDistances = [5, 10, 15];
            for (const verticalDist of verticalDistances) {
                results = await searchWithVerticalDistance(verticalDist, `Attempt with ${verticalDist}px vertical distance`);

                if (results.length > 0) {
                    //console.log(`Found ${results.length} results with vertical distance ${verticalDist}px`);
                    break; // Stop once we find matches
                }
            }

            if (results.length === 0) {
                //console.log('No results found after trying vertical distances: 5px, 10px, 15px');
            }



            //console.log(`Transaction search complete: found ${results.length} results`);

            // MODIFICATION: Sort results by position before setting them
            const sortedResults = sortResultsByPosition(results);
            setSearchResults(sortedResults);
            setCurrentSearchIndex(0);

            if (sortedResults.length > 0) {
                setPageNumber(sortedResults[0].page);
                setSearchHighlight(sortedResults[0].boundingBox);
            } else {
                setSearchHighlight(null);
            }
        } catch (error) {
            console.error('Transaction search error:', error);
        } finally {
            searchRunningRef.current = false;
            setIsSearching(false);
        }
    }, [transactionSearch, numPages]);


    // Trigger transaction search when transactionSearch prop changes
    useEffect(() => {
        // An authoritative coordinate bbox supersedes the fuzzy description+amount
        // fallback. When present, skip the search entirely — it matches the FIRST
        // same-description row (often the wrong page) and, being checked before
        // boundingBox in renderHighlight, would clobber the exact highlight after a
        // brief flash. Only clear a stale AUTO highlight (not an active manual search);
        // setSearchHighlight(null) is a no-op when already null (React bails).
        if (hasBoundingBox) {
            if (!searchText) {
                setSearchHighlight(null);
            }
            lastTransactionSearchRef.current = null;
            return;
        }
        if (!transactionSearch || !transactionSearch.descr || transactionSearch.amount === undefined) {
            // Clear if no valid transaction search
            if (!searchText) {
                setSearchHighlight(null);
                setSearchResults([]);
                setCurrentSearchIndex(0);
            }
            lastTransactionSearchRef.current = null;
            return;
        }

        // Check if transaction search actually changed
        const searchKey = JSON.stringify({
            descr: transactionSearch.descr,
            amount: transactionSearch.amount,
            posting_date: transactionSearch.posting_date
        });

        if (lastTransactionSearchRef.current === searchKey) {
            return; // Same search, don't re-run
        }

        // Wait for PDF to be fully loaded
        if (numPages && pdfDocumentRef.current && !searchRunningRef.current && canSearch) {
            lastTransactionSearchRef.current = searchKey;
            performTransactionSearch();
        }
    }, [hasBoundingBox, transactionSearch?.descr, transactionSearch?.amount, transactionSearch?.posting_date, numPages, performTransactionSearch, searchText, canSearch]);

    const nextSearchResult = useCallback(() => {
        if (searchResults.length === 0) return;
        const nextIndex = (currentSearchIndex + 1) % searchResults.length;
        setCurrentSearchIndex(nextIndex);
        setPageNumber(searchResults[nextIndex].page);
        setSearchHighlight(searchResults[nextIndex].boundingBox);
    }, [searchResults, currentSearchIndex]);

    const previousSearchResult = useCallback(() => {
        if (searchResults.length === 0) return;
        const prevIndex = (currentSearchIndex - 1 + searchResults.length) % searchResults.length;
        setCurrentSearchIndex(prevIndex);
        setPageNumber(searchResults[prevIndex].page);
        setSearchHighlight(searchResults[prevIndex].boundingBox);
    }, [searchResults, currentSearchIndex]);

    const clearSearch = useCallback(() => {
        setSearchText('');
        setSearchResults([]);
        setCurrentSearchIndex(0);
        setSearchHighlight(null);
        lastTransactionSearchRef.current = null;
    }, []);

    // Keyboard shortcuts
    useEffect(() => {
        const handleKeyPress = (event) => {
            // Ignore if user is typing in an input field
            if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA') {
                return;
            }

            switch (event.key) {
                case 'ArrowLeft':
                    event.preventDefault();
                    goToPreviousPage();
                    break;
                case 'ArrowRight':
                    event.preventDefault();
                    goToNextPage();
                    break;
                case 'PageUp':
                    event.preventDefault();
                    goToPreviousPage();
                    break;
                case 'PageDown':
                    event.preventDefault();
                    goToNextPage();
                    break;
                case '+':
                case '=':
                    event.preventDefault();
                    zoomIn();
                    break;
                case '-':
                case '_':
                    event.preventDefault();
                    zoomOut();
                    break;
                case '0':
                    event.preventDefault();
                    resetZoom();
                    break;
                default:
                    break;
            }
        };

        window.addEventListener('keydown', handleKeyPress);
        return () => window.removeEventListener('keydown', handleKeyPress);
    }, [goToPreviousPage, goToNextPage, zoomIn, zoomOut, resetZoom]);

    // Scroll-to-edge pagination
    useEffect(() => {
        if (!containerRef.current) return;

        const handleWheel = (event) => {
            const container = containerRef.current;
            if (!container) return;

            const now = Date.now();
            const timeSinceLastPagination = now - lastScrollPaginationRef.current;

            // Debounce: prevent pagination more than once per 500ms
            if (timeSinceLastPagination < 500) {
                return;
            }

            const { scrollTop, scrollHeight, clientHeight } = container;
            const scrollThreshold = 10; // pixels from edge to trigger pagination

            // Scrolling up at the top
            if (event.deltaY < 0 && scrollTop <= scrollThreshold && pageNumber > 1) {
                event.preventDefault();
                lastScrollPaginationRef.current = now;
                goToPreviousPage();
                // Scroll to bottom of new page after a brief delay
                setTimeout(() => {
                    if (container) {
                        container.scrollTop = container.scrollHeight - container.clientHeight;
                    }
                }, 100);
            }
            // Scrolling down at the bottom
            else if (event.deltaY > 0 && scrollTop + clientHeight >= scrollHeight - scrollThreshold && numPages && pageNumber < numPages) {
                event.preventDefault();
                lastScrollPaginationRef.current = now;
                goToNextPage();
                // Scroll to top of new page after a brief delay
                setTimeout(() => {
                    if (container) {
                        container.scrollTop = 0;
                    }
                }, 100);
            }
        };

        const container = containerRef.current;
        container.addEventListener('wheel', handleWheel, { passive: false });

        return () => {
            container.removeEventListener('wheel', handleWheel);
        };
    }, [pageNumber, numPages, goToPreviousPage, goToNextPage]);

    // Highlight rendering

    /**
     * Normalize bounding box to PDF points (72 DPI)
     * Attempts to detect if bbox is in 300 DPI and convert to 72 DPI
     *
     * @param {object} bbox - Bounding box with x, y, width, height
     * @param {object} pageViewport - Page viewport with width, height (at scale 1)
     * @returns {object} Normalized bounding box in PDF points
     */
    const normalizeBboxToPdfPoints = (bbox, pageViewport) => {
        if (!bbox || !pageViewport) return bbox;

        const DPI_RATIO = 300 / 72; // ≈ 4.1667
        const TOLERANCE = 1.2; // 20% tolerance for detection

        // Calculate if bbox coordinates seem too large for the page
        const maxX = bbox.x + bbox.width;
        const maxY = bbox.y + bbox.height;

        // Check multiple signals that suggest 300 DPI:
        const signals = {
            // Signal 1: Any coordinate exceeds page dimensions
            coordsExceed: bbox.x > pageViewport.width ||
                bbox.y > pageViewport.height ||
                maxX > pageViewport.width ||
                maxY > pageViewport.height,

            // Signal 2: Dimensions are suspiciously large relative to page
            // (e.g., a bbox width > 80% of page width is unusual for small highlights)
            dimensionsSuspicious: bbox.width > (pageViewport.width * 0.9) ||
                bbox.height > (pageViewport.height * 0.9),

            // Signal 3: If coordinates are roughly 4x what we'd expect
            // Check if dividing by DPI_RATIO puts them in reasonable range
            appearsScaled: (bbox.x / DPI_RATIO < pageViewport.width * TOLERANCE) &&
                (bbox.y / DPI_RATIO < pageViewport.height * TOLERANCE) &&
                ((bbox.x + bbox.width) / DPI_RATIO < pageViewport.width * TOLERANCE) &&
                ((bbox.y + bbox.height) / DPI_RATIO < pageViewport.height * TOLERANCE)
        };

        //console.log('BBox normalization analysis:', {
        //     original: bbox,
        //     pageViewport,
        //     signals,
        //     DPI_RATIO
        // });

        // If we detect 300 DPI (coords exceed page OR dimensions suspicious AND appears scaled)
        const is300DPI = signals.coordsExceed ||
            (signals.dimensionsSuspicious && signals.appearsScaled);

        if (is300DPI) {
            const normalized = {
                x: bbox.x / DPI_RATIO,
                y: bbox.y / DPI_RATIO,
                width: bbox.width / DPI_RATIO,
                height: bbox.height / DPI_RATIO,
            };
            //console.log('Detected 300 DPI, normalized:', normalized);
            return normalized;
        }

        //console.log('Assuming 72 DPI (PDF points), no conversion needed');
        return bbox;
    };

    // Opt-in auto-scroll: once the highlighted line is on the current rendered
    // page, bring it into view (centered-ish) so the reviewer doesn't have to
    // scroll to find it. Guarded to fire once per document+page so it never
    // fights the user's own scrolling.
    const autoScrolledKeyRef = useRef(null);
    useEffect(() => {
        if (!scrollHighlightIntoView) return;
        if (documentLoading || pageLoading || !numPages) return;
        if (!boundingBox || pageNumber !== boundingBoxPageRef.current) return;
        if (!containerRef.current || !currentViewport) return;
        // Include the nonce so a re-click recenters even on the same doc/page.
        const key = `${stableDocumentKey}:${pageNumber}:${scrollHighlightNonce}`;
        if (autoScrolledKeyRef.current === key) return;
        const hb = normalizeBboxToPdfPoints(boundingBox, currentViewport);
        if (!hb || typeof hb.y !== 'number') return;
        autoScrolledKeyRef.current = key;
        const container = containerRef.current;
        const targetTop = (hb.y * scale) - (container.clientHeight * 0.3);
        container.scrollTop = Math.max(0, targetTop);
    // boundingBox is derived fresh each render; the key guard prevents re-fire.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scrollHighlightIntoView, documentLoading, pageLoading, numPages, pageNumber, currentViewport, scale, stableDocumentKey, scrollHighlightNonce]);

    const renderHighlight = () => {
        //console.log('=== RENDER HIGHLIGHT DEBUG ===');
        //console.log('searchHighlight:', searchHighlight);
        //console.log('boundingBox prop:', boundingBox);
        //console.log('Current scale:', scale);
        //console.log('Current page number:', pageNumber);
        //console.log('Viewport (scale=1):', currentViewport);

        // Don't render highlight while PDF or page is loading
        if (documentLoading || pageLoading || !numPages || !pdfDocumentRef.current) {
            return null;
        }


        let highlightBox = null;
        let isOnCorrectPage = false;

        if (searchHighlight) {
            // Check if we're on the same page as the current search result
            if (searchResults.length > 0 && searchResults[currentSearchIndex]) {
                isOnCorrectPage = pageNumber === searchResults[currentSearchIndex].page;
            }
            if (isOnCorrectPage) {
                highlightBox = searchHighlight;
                //console.log('Using searchHighlight');
            }
        } else if (boundingBox) {
            // Check if we're on the page that boundingBox is associated with
            isOnCorrectPage = pageNumber === boundingBoxPageRef.current;
            if (isOnCorrectPage) {
                // Normalize the boundingBox prop to PDF points
                highlightBox = currentViewport
                    ? normalizeBboxToPdfPoints(boundingBox, currentViewport)
                    : boundingBox;
                //console.log('Using boundingBox prop (after normalization)');
            }
        }

        if (!highlightBox) {
            //console.log('No highlight to render');
            return null;
        }

        //console.log('Final highlightBox:', highlightBox);

        const renderedBox = {
            left: `${highlightBox.x * scale}px`,
            top: `${highlightBox.y * scale}px`,
            width: `${highlightBox.width * scale}px`,
            height: `${highlightBox.height * scale}px`,
        };

        //console.log('Rendered box:', renderedBox);
        //console.log('=== END DEBUG ===');

        return (
            <Box
                sx={{
                    position: 'absolute',
                    left: renderedBox.left,
                    top: renderedBox.top,
                    width: renderedBox.width,
                    height: renderedBox.height,
                    border: '1px solid',
                    // borderColor: searchHighlight ? 'warning.main' : 'primary.main',
                    // backgroundColor: searchHighlight ? 'rgba(255, 193, 7, 0.2)' : 'rgba(33, 150, 243, 0.2)',
                    borderColor: 'warning.main',
                    backgroundColor: 'rgba(255, 193, 7, 0.2)',
                    pointerEvents: 'none',
                    zIndex: 10,
                    boxSizing: 'border-box',
                }}
            />
        );
    };

    return (
        <Paper
            className={className}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                overflow: 'hidden'
            }}
        >
            {/* Toolbar */}
            <Toolbar
                sx={{
                    gap: 1,
                    minHeight: { xs: 56, sm: 64 },
                    borderBottom: 1,
                    borderColor: 'divider',
                    flexWrap: { xs: 'wrap', md: 'nowrap' }
                }}
            >
                {/* Navigation Controls */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Tooltip title="Previous Page (←, Page Up)">
                        <span>
                            <IconButton
                                onClick={goToPreviousPage}
                                disabled={pageNumber <= 1}
                                size="small"
                                color="primary"
                            >
                                <ChevronLeft />
                            </IconButton>
                        </span>
                    </Tooltip>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TextField
                            value={pageNumber}
                            onChange={(e) => goToPage(e.target.value)}
                            type="number"
                            size="small"
                            inputProps={{
                                min: 1,
                                max: numPages || 1,
                                style: { textAlign: 'center', width: '50px' }
                            }}
                        />
                        <Typography variant="body2" color="text.secondary">
                            / {numPages || '?'}
                        </Typography>
                    </Box>

                    <Tooltip title="Next Page (→, Page Down)">
                        <span>
                            <IconButton
                                onClick={goToNextPage}
                                disabled={!numPages || pageNumber >= numPages}
                                size="small"
                                color="primary"
                            >
                                <ChevronRight />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>

                <Divider orientation="vertical" flexItem />

                {/* Zoom Controls */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Tooltip title="Zoom Out (−)">
                        <span>
                            <IconButton
                                onClick={zoomOut}
                                disabled={scale <= 0.5}
                                size="small"
                                color="primary"
                            >
                                <ZoomOut />
                            </IconButton>
                        </span>
                    </Tooltip>

                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: '45px', textAlign: 'center' }}>
                        {Math.round(scale * 100)}%
                    </Typography>

                    <Tooltip title="Zoom In (+)">
                        <span>
                            <IconButton
                                onClick={zoomIn}
                                disabled={scale >= 3.0}
                                size="small"
                                color="primary"
                            >
                                <ZoomIn />
                            </IconButton>
                        </span>
                    </Tooltip>

                    <Tooltip title="Reset Zoom (0)">
                        <IconButton
                            onClick={resetZoom}
                            size="small"
                            color="primary"
                        >
                            <RestorePage />
                        </IconButton>
                    </Tooltip>

                    <Divider orientation="vertical" flexItem sx={{ mx: 0.5, height: 24, alignSelf: 'center' }} />

                    <Tooltip title="Fit to Width">
                        <IconButton
                            onClick={fitToWidth}
                            size="small"
                            color={fitMode === 'width' ? 'primary' : 'default'}
                            sx={fitMode === 'width' ? { bgcolor: 'action.selected' } : {}}
                        >
                            <WidthFull />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title="Fit to Page">
                        <IconButton
                            onClick={fitToPage}
                            size="small"
                            color={fitMode === 'page' ? 'primary' : 'default'}
                            sx={fitMode === 'page' ? { bgcolor: 'action.selected' } : {}}
                        >
                            <FitScreen />
                        </IconButton>
                    </Tooltip>
                </Box>

                <Divider orientation="vertical" flexItem />

                {/* Search Controls */}
                {!canSearch && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: { xs: 1, md: 0 } }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            This document is not searchable
                        </Typography>
                    </Box>
                )}
                {canSearch && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: { xs: 1, md: 0 } }}>
                        <TextField
                            placeholder="Search..."
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && performSearch()}
                            size="small"
                            sx={{ minWidth: '200px' }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        {(searchText || searchResults.length > 0) && (
                                            <IconButton
                                                onClick={clearSearch}
                                                size="small"
                                                edge="end"
                                                sx={{ mr: 0.5 }}
                                            >
                                                <Clear fontSize="small" />
                                            </IconButton>
                                        )}
                                        <IconButton
                                            onClick={performSearch}
                                            disabled={!searchText || isSearching}
                                            size="small"
                                            edge="end"
                                        >
                                            {isSearching ? <CircularProgress size={20} /> : <Search />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />

                        {searchResults.length > 0 && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                    {currentSearchIndex + 1} / {searchResults.length}
                                </Typography>
                                <Tooltip title="Previous Result">
                                    <IconButton
                                        onClick={previousSearchResult}
                                        size="small"
                                        color="primary"
                                    >
                                        <KeyboardArrowUp />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Next Result">
                                    <IconButton
                                        onClick={nextSearchResult}
                                        size="small"
                                        color="primary"
                                    >
                                        <KeyboardArrowDown />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        )}
                    </Box>
                )}

                <Box sx={{ flexGrow: 1 }} />

                {downloadError && (
                    <Typography
                        role="alert"
                        variant="caption"
                        color="error"
                        sx={{ mr: 2, maxWidth: 320 }}
                    >
                        {downloadError}
                    </Typography>
                )}

                {/* Download Button */}
                <Tooltip title="Download PDF">
                    <Button
                        onClick={downloadPdf}
                        variant="contained"
                        startIcon={<Download />}
                        size="small"
                    >
                        Download
                    </Button>
                </Tooltip>
            </Toolbar>

            {/* PDF Display Area */}
            <Box
                ref={containerRef}
                sx={{
                    flex: 1,
                    overflow: 'auto',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'flex-start',
                    p: 3,
                    bgcolor: 'grey.100'
                }}
            >
                <Box sx={{ position: 'relative' }} ref={pageContainerRef}>
                    <Document
                        key={stableDocumentKey}
                        file={documentFile}
                        // onLoad={()=>setPageLoading(true)}
                        // onLoadStart={()=>setPageLoading(true)}
                        onLoadSuccess={onDocumentLoadSuccess}
                        onLoadError={(error) => console.error('Error loading PDF:', error)}
                        error={
                            pdfError ? (
                                <Box sx={{ p: 5, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, maxWidth: 480 }}>
                                    <Typography>Failed to load PDF.</Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                                        {`${pdfError.message || pdfError}`}
                                    </Typography>
                                    <Button variant="outlined" size="small" onClick={retryPdfLoad} sx={{ minHeight: 44, minWidth: 44 }}>
                                        Retry
                                    </Button>
                                </Box>
                            ) : undefined
                        }
                        loading={
                            <Box sx={{ p: 5, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                                <CircularProgress />
                                <Typography>Loading PDF...</Typography>
                            </Box>
                        }
                        onItemClick={({ pageNumber: itemPageNumber }) => setPageNumber(itemPageNumber)}
                        onPassword={(callback) => {
                            const password = prompt('Enter PDF password:');
                            callback(password);
                        }}
                        options={PDF_DOCUMENT_OPTIONS}
                    >
                        <Page
                            pageNumber={pageNumber}
                            scale={scale}
                            renderTextLayer={true}
                            // Do NOT render the HTML annotation-layer overlay. Source
                            // documents (e.g. client-supplied bank statements) can carry
                            // embedded /Highlight markup annotations; pdf.js draws those as
                            // oversized, opaque overlay blocks that crowd out the underlying
                            // text. The correctly-sized highlights are still painted onto the
                            // page canvas (react-pdf renders with annotationMode: ENABLE), so
                            // the markup remains visible but readable. Matches
                            // DocumentSplitEditor.js. The viewer's own search/bbox highlight
                            // is a separate MUI overlay (renderHighlight) and is unaffected.
                            renderAnnotationLayer={false}
                            onRenderSuccess={() => setPageLoading(false)}
                            loading={
                                <Box sx={{ p: 5, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                                    <CircularProgress />
                                    <Typography>Loading page...</Typography>
                                </Box>
                            }
                        />
                    </Document>
                    {renderHighlight()}
                </Box>
            </Box>

            {/* Keyboard Shortcuts Help */}
            <Box
                sx={{
                    px: 2,
                    py: 1,
                    borderTop: 1,
                    borderColor: 'divider',
                    bgcolor: 'grey.50'
                }}
            >
                <Typography variant="caption" color="text.secondary">
                    Keyboard shortcuts: ← → PgUp PgDn (navigate) | + − (zoom) | 0 (reset zoom) | Scroll past edge to change pages
                </Typography>
            </Box>
        </Paper>
    );
};

// Wrap with React.memo to prevent unnecessary re-renders when props haven't changed
export default React.memo(PdfViewer, (prevProps, nextProps) => {
    // Return true to skip re-render if props are equal
    // Ignore function props like onPageChange since they may have new references but same behavior

    // Use documentKey if provided, otherwise fall back to URL comparison
    const documentIdentifierChanged = nextProps.documentKey
        ? (prevProps.documentKey !== nextProps.documentKey)
        : (prevProps.url !== nextProps.url);

    // Debug logging
    const changed = {
        documentIdentifier: documentIdentifierChanged,
        displayPage: prevProps.displayPage !== nextProps.displayPage,
        navigationKey: prevProps.navigationKey !== nextProps.navigationKey,
        className: prevProps.className !== nextProps.className,
        boundingBox: JSON.stringify(prevProps.boundingBox) !== JSON.stringify(nextProps.boundingBox),
        transactionSearch: JSON.stringify(prevProps.transactionSearch) !== JSON.stringify(nextProps.transactionSearch)
    };

    const hasChanges = Object.values(changed).some(v => v);
    if (hasChanges) {
        //console.log('PdfViewer re-rendering due to prop changes:', changed);
    } else {
        //console.log('PdfViewer skipping re-render - no prop changes');
    }

    // Return true to SKIP re-render, false to re-render
    return !hasChanges;
});